// The size of the drawing surface, shared by both backends.
//
// Reading the CSS box, capping the device pixel ratio and resizing the
// backing store is the same work whichever API draws. It used to sit in
// `gl/context.ts`; it lives here so the WebGPU context does not copy it.
//
// The dpr watcher is the one subtle part. A `matchMedia` query on the
// CURRENT ratio fires once when the window moves to a screen of another
// ratio, so the listener is rebuilt after every change.
//
// The ratio the surface draws at is the device ratio under its cap, times
// the render scale of the setting (R484 item 2, `render/dpr.ts`). The cap
// is `MAX_DPR`, or the `?dpr=N` knob that the reel recorder, the visual
// diff, the store shots and the throughput tools state. A change of the
// render scale sizes the backing store again at once.

import { readQueryKnobs } from '../knobs';
import { deviceRatio, MAX_DPR, onRenderScale, renderScale } from '../../render/dpr';

/** Does the query string carry this flag? Both contexts read `?glcheck` so. */
export function hasQueryFlag(name: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has(name);
  } catch {
    return false;
  }
}

/**
 * The measured size of one canvas.
 *
 * The owner builds one, reads `width`/`height`/`pixelWidth`/`pixelHeight`
 * off it, and is called back through `onResize` after every change.
 */
export class Surface {
  width = 1;
  height = 1;
  pixelWidth = 1;
  pixelHeight = 1;
  dpr = 1;

  /** The cap `measure` applies, which `?dpr=N` may lower. */
  private readonly maxDpr: number;
  private readonly resizeFns: Array<() => void> = [];
  private observer: ResizeObserver | null = null;
  private dprQuery: MediaQueryList | null = null;
  /** The armed `change` listener of `dprQuery`, so `dispose` can take it off. */
  private dprOnce: (() => void) | null = null;
  /** Takes the render scale listener off. */
  private readonly stopScale: () => void;
  private disposed = false;

  constructor(readonly canvas: HTMLCanvasElement, maxDpr?: number) {
    this.maxDpr = maxDpr ?? readQueryKnobs().dpr ?? MAX_DPR;
    this.watchSize();
    this.watchDpr();
    this.stopScale = onRenderScale(() => this.measure());
    this.measure();
  }

  onResize(fn: () => void): void { this.resizeFns.push(fn); }

  /** Reads the CSS box and the ratio, and resizes the backing store. */
  measure(): void {
    if (this.disposed) return;
    const rect = this.canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.round(rect.width || this.canvas.clientWidth || 1));
    const cssH = Math.max(1, Math.round(rect.height || this.canvas.clientHeight || 1));
    const dpr = deviceRatio(this.maxDpr) * renderScale();
    const pw = Math.max(1, Math.round(cssW * dpr));
    const ph = Math.max(1, Math.round(cssH * dpr));
    if (cssW === this.width && cssH === this.height
      && pw === this.pixelWidth && ph === this.pixelHeight) {
      return;
    }
    this.width = cssW;
    this.height = cssH;
    this.dpr = dpr;
    this.pixelWidth = pw;
    this.pixelHeight = ph;
    this.canvas.width = pw;
    this.canvas.height = ph;
    for (const fn of this.resizeFns) fn();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.observer?.disconnect();
    this.stopScale();
    // The dpr watcher goes too, so a later screen change arms nothing (R1347).
    if (this.dprOnce) this.dprQuery?.removeEventListener('change', this.dprOnce);
    this.dprOnce = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWindowResize);
    }
  }

  // -- the private half ------------------------------------------------------

  private watchSize() {
    if (typeof ResizeObserver === 'undefined') {
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', this.onWindowResize);
      }
      return;
    }
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.canvas);
  }

  private onWindowResize = () => this.measure();

  private watchDpr() {
    if (this.disposed || typeof window === 'undefined' || !window.matchMedia) return;
    const ratio = deviceRatio();
    this.dprQuery = window.matchMedia(`(resolution: ${ratio}dppx)`);
    const once = () => {
      this.dprQuery?.removeEventListener('change', once);
      this.dprOnce = null;
      this.measure();
      this.watchDpr();
    };
    this.dprOnce = once;
    this.dprQuery.addEventListener('change', once);
  }
}
